import { fetch } from '../common/api/helpers.ts';

const CONCURRENT_FILE_UPLOAD_LIMIT = 3;
const PACKET_SIZE = 1000 * 1000 * 5; // 5MB

interface Progress {
  finishedPackets: number;
  totalPackets: number;
  file: File;
}

type ProgressCallback = (progress: Progress[]) => void;
type OnUploadFinishCallback = () => void;
type OnErrorCallback = (error: Error) => void;

interface ReadyFile {
  id: string;
  filename: string;
  contentType: string;
  packets: Uint8Array[];
  index: number;
}

class NewFileUploader {
  groupId: string;
  authData: { [key: string]: string };
  files: File[];
  url: string;
  filesReadyToUpload: ReadyFile[];
  nonFileParams?: { [key: string | number | symbol]: unknown };
  progress: Progress[];
  packetsCurrentlyUploading: number;
  finishedFileCount: number;
  progressCallback?: ProgressCallback;
  onUploadFinishCallback?: OnUploadFinishCallback;
  onErrorCallback?: OnErrorCallback;

  constructor(
    files: FileList,
    url: string,
    nonFileParams?: { [key: string | number | symbol]: unknown }
  ) {
    this.groupId = crypto.randomUUID();
    this.authData = JSON.parse(localStorage.getItem('auth_data')!);
    this.files = Array.from(files);
    this.url = url;
    this.filesReadyToUpload = [];
    this.nonFileParams = nonFileParams;
    this.progress = Array.from(this.files).map(file => ({
      file,
      totalPackets: 0,
      finishedPackets: 0,
    }));
    this.packetsCurrentlyUploading = 0;
    this.finishedFileCount = 0;
  }

  upload = async () => {
    try {
      await this.readFiles();
      await this.sendFiles();
    } catch (err) {
      this.onErrorCallback?.(err as Error) ?? console.error(err);
    }
  };

  readFiles = async () => {
    const promises = [];

    for (const [index, file] of this.files.entries())
      promises.push(this.convertFile(file, index));

    await Promise.all(promises);
  };

  convertFile = (file: File, index: number) =>
    new Promise<void>((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onloadend = () => {
          if (!reader?.result) return;
          const bytes = new Uint8Array(reader.result as ArrayBuffer);
          const packets: Uint8Array[] = [];

          for (let i = 0; i < bytes.length; i += PACKET_SIZE)
            packets.push(bytes.slice(i, i + PACKET_SIZE));

          this.progress[index].totalPackets = packets.length;

          this.filesReadyToUpload.push({
            id: crypto.randomUUID(),
            filename: file.name,
            contentType: file.type,
            packets,
            index,
          });
          resolve();
        };
      } catch (err) {
        reject(err);
      }
    });

  sendFiles = async () => {
    for (const readyFile of this.filesReadyToUpload)
      await this.sendFile(readyFile);
  };

  sendFile = async (file: ReadyFile) => {
    const { packets } = file;
    const packetQueue = [];

    for (let i = 0; i < packets.length; i++) {
      while (this.packetsCurrentlyUploading >= CONCURRENT_FILE_UPLOAD_LIMIT)
        await this.wait();

      packetQueue.push(this.sendPacket(file, i));
    }

    await Promise.all(packetQueue);
  };

  sendPacket = async (file: ReadyFile, index: number) => {
    this.packetsCurrentlyUploading++;
    const { packets } = file;
    const totalBytes = this.files.reduce((sum, file) => sum + file.size, 0);
    //@ts-ignore
    const blob = new Blob([packets[index]], {
      type: 'application/octet-stream',
    });

    const form = new FormData();
    form.append('packet', blob);

    Object.entries(this.nonFileParams ?? {}).forEach(([key, value]: any) =>
      form.append(key, value)
    );

    Object.entries(file)
      .filter(([key]) => key !== 'packets' && key !== 'index')
      .forEach(([key, value]: any) => form.append(key, value));

    form.append('totalPackets', packets.length.toString());
    form.append('packetSize', PACKET_SIZE.toString());
    form.append('groupId', this.groupId);
    form.append('index', index.toString());
    form.append('totalFiles', this.files.length.toString());
    form.append('totalBytes', totalBytes.toString());

    await fetch(this.url, 'POST', form);

    this.progress[file.index].finishedPackets++;
    this.progressCallback?.(JSON.parse(JSON.stringify(this.progress)));
    this.packetsCurrentlyUploading--;

    if (packets.length - 1 === index) this.finishedFileCount++;

    if (
      this.files.length === this.finishedFileCount &&
      packets.length - 1 === index
    )
      this.onUploadFinishCallback?.();
  };

  wait = (milliseconds?: number) =>
    new Promise(resolve => {
      setTimeout(resolve, milliseconds ?? 250);
    });

  setOnErrorCallback = (cb: OnErrorCallback) => (this.onErrorCallback = cb);
  setProgressCallback = (cb: ProgressCallback) => (this.progressCallback = cb);
  setOnUploadFinishCallback = (cb: OnUploadFinishCallback) =>
    (this.onUploadFinishCallback = cb);
}

export default NewFileUploader;
